import React, {
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import BackgroundAnimationController from "./BackgroundAnimationController";
import CharacterAnimationController from "./CharacterAnimationController";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import { useSelector, useDispatch } from "react-redux";
import {
  selectIsFeatureActive,
  selectIntroPopupActive,
  selectMenuActive,
  selectIsSpinning,
  selectRoyalCollectTransitionActive,
} from "../../../State/selectors";
import {
  setRoyalCollectActive,
  setRoyalCollectTransitionActive,
  setRoyalCollectTriggered,
  setCriticalAnimationPlaying,
  setUserInterfaceDisabled,
} from "../../../State/actions";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useSlotRegistry } from "../../../Contexts/SlotRegistryContext";
import { useAppActivity } from "../../../Contexts/AppActivityContext";
import { useInitialIdleAnimations } from "../../../Hooks/useLandingAnimations";
import {
  attemptFullscreenToggle,
  isPhysicalDesktop,
  isInFullscreen,
} from "../../../utilities";
import store from "../../../State/Store/store";

// Rate limiting for background clicks - prevents performance degradation from rapid clicking
let lastBackgroundClickTime = 0;
const MIN_BACKGROUND_CLICK_GAP = 100; // Same throttling as play buttons (~10 clicks/sec max)

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let backgroundClickThrottleActive = false;
let backgroundClickThrottleTimeout = null;

const useQuadrantSprites = FEATURES_CONFIG.BACKGROUND?.QUADRANT_SPRITES !== false;

const BackgroundAnimation = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const backgroundControllerRef = useRef(null);
  const characterControllerRef = useRef(null);
  const backgroundContainerRef = useRef(null);
  const characterContainerRef = useRef(null);
  const backgroundSpriteTopLeftRef = useRef(null);
  const backgroundSpriteTopRightRef = useRef(null);
  const backgroundSpriteBottomLeftRef = useRef(null);
  const backgroundSpriteBottomRightRef = useRef(null);
  const { dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation, isMobileSmall } =
    useDimensions();
  const { backgroundAnimationControllerRef, characterAnimationControllerRef } = useReelRefs();
  const slotRegistry = useSlotRegistry();
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();

  const isFeatureActive = useSelector(selectIsFeatureActive);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const menuActive = useSelector(selectMenuActive);
  const royalCollectTransitionActive = useSelector(selectRoyalCollectTransitionActive);

  // Select textures based on Hold and Win active state (4 quadrants) — only when quadrant sprites are enabled
  const textures = useMemo(() => {
    if (!useQuadrantSprites) return null;
    const prefix = isFeatureActive ? "feature" : "base";
    return {
      topLeft: PIXI.Assets.get(ASSET_NAME[`${prefix}_top_left`]),
      topRight: PIXI.Assets.get(ASSET_NAME[`${prefix}_top_right`]),
      bottomLeft: PIXI.Assets.get(ASSET_NAME[`${prefix}_bottom_left`]),
      bottomRight: PIXI.Assets.get(ASSET_NAME[`${prefix}_bottom_right`]),
    };
  }, [isFeatureActive]);

  // Initialize BackgroundAnimationController when assets are ready
  useEffect(() => {
    const spritesReady = useQuadrantSprites
      ? backgroundSpriteTopLeftRef.current &&
        backgroundSpriteTopRightRef.current &&
        backgroundSpriteBottomLeftRef.current &&
        backgroundSpriteBottomRightRef.current
      : true; // No sprites needed in spine-only mode

    if (
      backgroundContainerRef.current &&
      spritesReady &&
      !backgroundControllerRef.current
    ) {
      backgroundControllerRef.current = new BackgroundAnimationController(
        backgroundContainerRef.current,
        useQuadrantSprites
          ? {
              topLeft: backgroundSpriteTopLeftRef.current,
              topRight: backgroundSpriteTopRightRef.current,
              bottomLeft: backgroundSpriteBottomLeftRef.current,
              bottomRight: backgroundSpriteBottomRightRef.current,
            }
          : null,
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        device,
        orientation,
        slotRegistry,
      );

      // Store controller in context ref for access from other components
      backgroundAnimationControllerRef.current =
        backgroundControllerRef.current;
    }

    // Initialize character controller (only when feature is enabled)
    if (
      FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED &&
      characterContainerRef.current &&
      !characterControllerRef.current
    ) {
      characterControllerRef.current = new CharacterAnimationController(
        characterContainerRef.current,
        symbolWidth,
        symbolHeight,
        symbolScale,
        dimensions,
        device,
        orientation,
      );

      // Link character to background controller for coordinated transitions
      if (backgroundControllerRef.current) {
        backgroundControllerRef.current.characterController = characterControllerRef.current;
      }

      // Store in context ref for access from any hook/component
      characterAnimationControllerRef.current = characterControllerRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Royal Collect transition: play feature_transition → set royalCollectActive
  useEffect(() => {
    if (
      royalCollectTransitionActive &&
      backgroundControllerRef.current
    ) {
      backgroundControllerRef.current.playFeatureTransition().then(() => {
        // Base game is off-screen — transition win animations to idle
        triggerInitialIdleAnimations(true);
        dispatch(setRoyalCollectTransitionActive(false));
        dispatch(setRoyalCollectActive(true));
        // Reset trigger flag here (not in the dialog controller) — mirrors how
        // FreeSpinSceneTransitionController resets freeSpinTriggered during transition
        dispatch(setRoyalCollectTriggered(false));
        dispatch(setCriticalAnimationPlaying(false));
        dispatch(setUserInterfaceDisabled(false));
      });
    }
  }, [royalCollectTransitionActive, dispatch, triggerInitialIdleAnimations]);

  // Expose controller methods via ref
  useImperativeHandle(
    ref,
    () => ({
      getController: () => backgroundControllerRef.current,
      getCharacterController: () => characterControllerRef.current,
      playBaseIdle: () => backgroundControllerRef.current?.playBaseIdle(),
      playFeatureIdle: () => backgroundControllerRef.current?.playFeatureIdle(),
      playBaseEventIn: () => backgroundControllerRef.current?.playBaseEventIn(),
      playBaseEventOut: () =>
        backgroundControllerRef.current?.playBaseEventOut(),
      playFeatureTransition: () =>
        backgroundControllerRef.current?.playFeatureTransition(),
      // Character methods
      playCharacterIdle: () => characterControllerRef.current?.playIdle(),
      playCharacterAnimation: (name) => characterControllerRef.current?.playAnimation(name),
    }),
    [],
  );

  // Handle dimension changes for responsive design
  useEffect(() => {
    // Resolve real device (compatDevice maps MOBILE_SMALL → MOBILE)
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;

    if (backgroundControllerRef.current) {
      backgroundControllerRef.current.resizeBackground(
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        realDevice,
        orientation,
      );
    }
    if (characterControllerRef.current) {
      characterControllerRef.current.resize(
        symbolWidth,
        symbolHeight,
        symbolScale,
        dimensions,
        realDevice,
        orientation,
      );
    }
  }, [dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation, isMobileSmall]);

  // Hide character while intro popup or menu is active.
  // Skip show() when a Royal Collect transition is playing — the transition
  // animation contains the character visually, and its spine completion handler
  // calls show() synchronously (in sync with the ticker, avoiding stale-pose artifacts).
  // We also guard against duplicate show() calls when the character is already visible.
  useEffect(() => {
    if (!FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) return;
    const controller = characterControllerRef.current;
    if (!controller) return;

    if (introPopupActive || menuActive) {
      controller.hide();
    } else if (!royalCollectTransitionActive) {
      // Only show if not already visible — the spine transition completion
      // handler may have already called show() synchronously
      if (controller.characterSpine && !controller.characterSpine.visible) {
        controller.show();
      }
    }
  }, [introPopupActive, menuActive, royalCollectTransitionActive]);

  // Spin idle detection
  const isSpinning = useSelector(selectIsSpinning);
  const { registerSpin, spinIdleTick } = useAppActivity();

  // Register spin when spinning starts and cancel pending go_go_go
  useEffect(() => {
    if (isSpinning) {
      registerSpin();
      // Cancel any pending go_go_go (but don't interrupt one that's actively playing)
      if (characterControllerRef.current) {
        characterControllerRef.current.cancelPending();
      }
    }
  }, [isSpinning, registerSpin]);

  // Play go_go_go on each idle tick — queued so it waits for idle_0 to finish
  useEffect(() => {
    if (!FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) return;
    if (spinIdleTick === 0) return;
    if (characterControllerRef.current && !introPopupActive && !menuActive && !isFeatureActive) {
      characterControllerRef.current.queueAnimation("go_go_go");
    }
  }, [spinIdleTick, introPopupActive, menuActive, isFeatureActive]);

  // Update textures when Hold and Win state changes (only when quadrant sprites are enabled)
  useEffect(() => {
    if (!useQuadrantSprites) return;
    if (
      backgroundSpriteTopLeftRef.current &&
      backgroundSpriteTopRightRef.current &&
      backgroundSpriteBottomLeftRef.current &&
      backgroundSpriteBottomRightRef.current &&
      textures?.topLeft &&
      textures?.topRight &&
      textures?.bottomLeft &&
      textures?.bottomRight
    ) {
      backgroundSpriteTopLeftRef.current.texture = textures.topLeft;
      backgroundSpriteTopRightRef.current.texture = textures.topRight;
      backgroundSpriteBottomLeftRef.current.texture = textures.bottomLeft;
      backgroundSpriteBottomRightRef.current.texture = textures.bottomRight;

      // Trigger controller to reposition sprites after texture change
      if (backgroundControllerRef.current) {
        backgroundControllerRef.current.applyResponsiveBackgroundScaling();

        // Also ensure correct animation is playing after texture change
        if (backgroundControllerRef.current.backgroundSpine?.state) {
          if (isFeatureActive) {
            backgroundControllerRef.current.playFeatureIdle();
          } else {
            backgroundControllerRef.current.playBaseIdle();
          }
        }
      }
    }
  }, [textures, isFeatureActive]);

  // Play appropriate idle animation based on feature state (Hold and Win or Royal Collect)
  // NOTE: orientation is NOT a dependency — resizeBackground() already handles
  // logo visibility via setLogoMode(). Including orientation here would reset
  // the animation mid-playback on device rotation (e.g. killing feature_transition).
  useEffect(() => {
    if (!backgroundControllerRef.current) {
      return;
    }

    // Only clear track 0 (idle animation), leave track 1 (tension) intact
    if (backgroundControllerRef.current.backgroundSpine?.state) {
      backgroundControllerRef.current.backgroundSpine.state.clearTrack(0);
    }

    // Toggle logo visibility (base/feature × landscape/portrait)
    backgroundControllerRef.current.setLogoMode(isFeatureActive, orientation);

    if (isFeatureActive) {
      // Play feature_idle when any feature mode is active
      backgroundControllerRef.current.playFeatureIdle();
    } else {
      // Play base_idle for base game
      backgroundControllerRef.current.playBaseIdle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureActive]);

  // Handle background click to toggle fullscreen and close MiniPaytable popup
  const handleBackgroundClick = useCallback(
    (event) => {
      // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
      // Prevents performance degradation from rapid clicking (50+ clicks/sec)
      if (backgroundClickThrottleActive) {
        return; // Instant exit - zero overhead, no logs, no code execution
      }

      // Set throttle flag IMMEDIATELY and schedule single reset
      backgroundClickThrottleActive = true;
      if (backgroundClickThrottleTimeout) {
        clearTimeout(backgroundClickThrottleTimeout);
      }
      backgroundClickThrottleTimeout = setTimeout(() => {
        backgroundClickThrottleActive = false;
        backgroundClickThrottleTimeout = null;
      }, MIN_BACKGROUND_CLICK_GAP);

      // Secondary rate limiting check
      const now = Date.now();
      const timeSinceLastClick = now - lastBackgroundClickTime;
      if (timeSinceLastClick < MIN_BACKGROUND_CLICK_GAP) {
        return;
      }
      lastBackgroundClickTime = now;

      // Stop event propagation to prevent bubbling
      if (event?.stopPropagation) {
        event.stopPropagation();
      }

      // MiniPaytable's background click area handles closing symbol popups
      // No need to dispatch closeSymbolPopup here

      // GUARD: Only attempt fullscreen toggle if conditions might be met
      // Check device type and fullscreen state BEFORE calling the function
      // This prevents unnecessary lazy imports, state reads, and function calls
      const isActualDesktop = isPhysicalDesktop();
      const alreadyInFullscreen = isInFullscreen();
      const fullscreenSupported = store.getState().settings.fullscreenSupported;

      if (!isActualDesktop && !alreadyInFullscreen && fullscreenSupported) {
        attemptFullscreenToggle(dispatch, device);
      }
    },
    [dispatch, device],
  );

  // Memoize sprite dimensions for click detection sprite - prevents recalculation on every render
  const spriteDimensions = useMemo(
    () => ({
      width: dimensions.width,
      height: dimensions.height,
    }),
    [dimensions.width, dimensions.height],
  );

  // Cleanup throttle timeout on unmount
  useEffect(() => {
    return () => {
      if (backgroundClickThrottleTimeout) {
        clearTimeout(backgroundClickThrottleTimeout);
        backgroundClickThrottleTimeout = null;
        backgroundClickThrottleActive = false;
      }
    };
  }, []);

  return (
    <Container ref={backgroundContainerRef} name="background" sortableChildren zIndex={0}>
      {/* Background quadrants - positioned as puzzle pieces (disabled when spine-only) */}
      {/* Position is controlled exclusively by BackgroundAnimationController.applyResponsiveBackgroundScaling() */}
      {/* Do NOT set x/y props here — React reconciliation would reset positions to (0,0) on every */}
      {/* re-render, creating a visible frame of misalignment before the useEffect repositions them. */}
      {useQuadrantSprites && (
        <Container name="background_quadrants" anchor={0.5}>
          {/* Top-left quadrant */}
          <Sprite
            ref={backgroundSpriteTopLeftRef}
            name="background_top_left"
            texture={textures.topLeft}
            anchor={[1, 1]}
            zIndex={0}
          />
          {/* Top-right quadrant */}
          <Sprite
            ref={backgroundSpriteTopRightRef}
            name="background_top_right"
            texture={textures.topRight}
            anchor={[0, 1]}
            zIndex={0}
          />
          {/* Bottom-left quadrant */}
          <Sprite
            ref={backgroundSpriteBottomLeftRef}
            name="background_bottom_left"
            texture={textures.bottomLeft}
            anchor={[1, 0]}
            zIndex={0}
          />
          {/* Bottom-right quadrant */}
          <Sprite
            ref={backgroundSpriteBottomRightRef}
            name="background_bottom_right"
            texture={textures.bottomRight}
            anchor={[0, 0]}
            zIndex={0}
          />
        </Container>
      )}
      {/* Character — inside background container so it shares the same coordinate space */}
      {FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED && (
        <Container ref={characterContainerRef} name="character_animation" zIndex={5} sortableChildren />
      )}
      {/* Full-screen invisible sprite for click detection - catches clicks on empty areas and background */}
      {/* Disabled during Hold and Win - nothing actionable on background click, and eventMode="static"
          on a full-screen sprite causes PixiJS to hit-test the entire canvas on every pointer event,
          which at 50+ clicks/sec blocks the RAF handler and causes animation jank */}
      {FEATURES_CONFIG.BACKGROUND.FULLSCREEN_CLICK_SPRITE && (
        <Sprite
          texture={PIXI.Texture.EMPTY}
          x={0}
          y={0}
          width={spriteDimensions.width}
          height={spriteDimensions.height}
          zIndex={0}
          eventMode={isFeatureActive ? "none" : "static"}
          cursor="pointer"
          pointertap={handleBackgroundClick}
        />
      )}
    </Container>
  );
});

BackgroundAnimation.displayName = "BackgroundAnimation";

export default BackgroundAnimation;
